import React, { useEffect, useRef } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { filterFns } from "@tanstack/react-table";
import { Interview, calculateTotalCost } from "@/services/interviewService";
import { EditableCellRenderer } from "./EditableCellRenderer";
import { AnonymizedAttributionCell } from "./AnonymizedAttributionCell";
import { InterviewDescriptionCell } from "./InterviewDescriptionCell";
import {
  ColumnHeader,
  FilterValue,
  SearchFilter,
  ExactFilter,
} from "./ColumnHeader";
import { Checkbox } from "@/components/ui/checkbox";
import { formatCurrency } from "./utils";
import { StarRatingCell, StarRatingCellRef } from "./StarRatingCell";
import { TranscriptStatusCell } from "./TranscriptStatusCell";

// Custom filter function that supports both search and multi-select exact matching
export const multiSelectFilter = (
  row: any,
  columnId: string,
  filterValue: any
) => {
  const rowValue = row.getValue(columnId);

  // If no filter value, show all rows
  if (!filterValue) return true;

  // Convert row value to string for comparison
  const rowValueStr = String(rowValue || "").toLowerCase();

  const typedFilterValue = filterValue as FilterValue;

  // Handle structured filter objects that distinguish between search and exact selection
  if (
    typedFilterValue &&
    typeof typedFilterValue === "object" &&
    !Array.isArray(typedFilterValue)
  ) {
    if ((typedFilterValue as SearchFilter).type === "search") {
      // Search mode: use contains logic
      const searchStr = String(
        (typedFilterValue as SearchFilter).value || ""
      ).toLowerCase();
      return rowValueStr.includes(searchStr);
    }
    if ((typedFilterValue as ExactFilter).type === "exact") {
      // Exact selection mode: use exact matching
      const values = (typedFilterValue as ExactFilter).values || [];
      return values.some((val: any) => {
        const filterStr = String(val || "").toLowerCase();
        return rowValueStr === filterStr;
      });
    }
  }

  // Legacy support: If filterValue is an array (multi-select), check if rowValue exactly matches any of the selected values
  if (Array.isArray(typedFilterValue)) {
    return typedFilterValue.some((val) => {
      const filterStr = String(val || "").toLowerCase();
      return rowValueStr === filterStr; // Exact match
    });
  }

  // Legacy support: If filterValue is a string (search), use contains logic for search functionality
  if (typeof typedFilterValue === "string") {
    const filterStr = String(typedFilterValue || "").toLowerCase();
    return rowValueStr.includes(filterStr);
  }

  return true;
};

// Simple header for computed columns (no filtering, just sorting)
const SimpleColumnHeader: React.FC<{ column: any; title: string }> = ({
  column,
  title,
}) => {
  const sortedIndex = column.getSortIndex();
  const isSorted = column.getIsSorted();

  return (
    <div className="flex items-center gap-1 cursor-pointer w-full">
      <button
        type="button"
        className="flex items-center gap-1 group text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300"
        onClick={(e) => {
          // Get current sorting state from the table
          const table = (column as any).getTable?.();
          const currentSorting = table ? table.getState().sorting || [] : [];
          const tableMeta = table ? (table.options.meta as any) : {};
          const dateSortActivated = tableMeta?.dateSortActivated || false;

          // Check if we're in the default state (date descending only AND not intentionally activated)
          const isDefaultSort =
            currentSorting.length === 1 &&
            currentSorting[0].id === "date" &&
            currentSorting[0].desc === true &&
            !dateSortActivated;

          // If in default state, replace with single sort; otherwise use multi-sort
          const useMultiSort = !isDefaultSort;

          column.toggleSorting(undefined, useMultiSort);
        }}
        title="Click to sort, Shift+Click for multi-sort"
      >
        {title}
        <span className="flex flex-col ml-1">
          <svg
            className={`h-3 w-3 ${
              column.getIsSorted() === "asc"
                ? "text-green-600"
                : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" />
          </svg>
          <svg
            className={`h-3 w-3 -mt-1 ${
              column.getIsSorted() === "desc"
                ? "text-green-600"
                : "text-gray-300"
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
          </svg>
        </span>
      </button>
      {column.getIsSorted() && (
        <div className="w-2 h-2 bg-green-600 rounded-full ml-1"></div>
      )}
      {isSorted && sortedIndex > 0 && (
        <span className="text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded font-semibold">
          {sortedIndex + 1}
        </span>
      )}
    </div>
  );
};

/**
 * Creates and returns the column definitions for the interview table
 */
export const createColumns = (
  onOpenChat?: (interviewId?: string) => void
): ColumnDef<Interview>[] =>
  [
    {
      id: "select",
      header: ({ table }) => {
        const ref = useRef<HTMLInputElement>(null);
        useEffect(() => {
          if (ref.current) {
            ref.current.indeterminate = table.getIsSomePageRowsSelected();
          }
        }, [table.getIsSomePageRowsSelected()]);
        return (
          <input
            ref={ref}
            type="checkbox"
            id="select-all-rows"
            name="select-all-rows"
            checked={table.getIsAllPageRowsSelected()}
            onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
            aria-label="Select all"
            className="form-checkbox h-4 w-4 text-green-600 transition duration-150 ease-in-out border-gray-300 rounded focus:outline-none"
          />
        );
      },
      cell: ({ row }) => (
        <Checkbox
          id={`select-row-${row.id}`}
          name={`select-row-${row.id}`}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={(checked) => row.toggleSelected(!!checked)}
          aria-label="Select row"
        />
      ),
      size: 48,
      minSize: 48,
      maxSize: 48,
      enableSorting: false,
      enableResizing: false,
    },
    {
      accessorKey: "status",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Status" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 120,
    },
    {
      id: "transcript",
      accessorKey: "transcriptStatus",
      enableColumnFilter: false,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Transcript" />
      ),
      cell: (info) => {
        const meta = info.table.options.meta as any;
        const handleUpdate = meta?.onUpdate;
        const transcriptRefs = meta?.transcriptRefs;
        const onOpenTranscriptModal = meta?.onOpenTranscriptModal;

        return handleUpdate ? (
          <TranscriptStatusCell
            ref={(el) => {
              if (transcriptRefs && el) {
                transcriptRefs.current[info.row.original.id] = el;
              }
            }}
            {...info}
            onUpdate={handleUpdate}
            onOpenChat={onOpenChat}
            onOpenTranscriptModal={onOpenTranscriptModal}
          />
        ) : (
          <div className="flex items-center justify-center text-gray-400">
            -
          </div>
        );
      },
      enableResizing: true,
      size: 120,
      enableSorting: true,
      sortingFn: (rowA, rowB, columnId) => {
        const statusOrder = { ready: 3, processing: 2, error: 1, null: 0 };
        const a = statusOrder[(rowA.getValue(columnId) as any) || "null"];
        const b = statusOrder[(rowB.getValue(columnId) as any) || "null"];
        return a - b;
      },
    },
    {
      accessorKey: "expertRating",
      enableColumnFilter: false,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Expert Rating" />
      ),
      cell: (info) => {
        const meta = info.table.options.meta as any;
        const handleUpdate = meta?.onUpdate;
        const ratingRefs = meta?.ratingRefs;

        return handleUpdate ? (
          <StarRatingCell
            ref={(el) => {
              if (ratingRefs && el) {
                ratingRefs.current[info.row.original.id] = el;
              }
            }}
            info={info}
            onUpdate={handleUpdate}
          />
        ) : (
          <div className="flex items-center justify-center text-gray-400">
            No rating
          </div>
        );
      },
      enableResizing: true,
      size: 100,
      enableSorting: true,
      sortingFn: (rowA, rowB, columnId) => {
        const a = (rowA.getValue(columnId) as number) || 0;
        const b = (rowB.getValue(columnId) as number) || 0;
        return a - b;
      },
    },
    {
      accessorKey: "anonymizedAttribution",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader
          column={column}
          table={table}
          title="Anonymized Attribution"
        />
      ),
      cell: AnonymizedAttributionCell,
      enableResizing: true,
      size: 180,
    },
    {
      accessorKey: "interviewDescription",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader
          column={column}
          table={table}
          title="Interview Description"
        />
      ),
      cell: InterviewDescriptionCell,
      enableResizing: true,
      size: 250,
    },
    {
      accessorKey: "expertFirstName",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="First Name" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 150,
    },
    {
      accessorKey: "expertLastName",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Last Name" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 150,
    },
    {
      accessorKey: "company",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Company" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 150,
    },
    {
      accessorKey: "title",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Title" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 150,
    },
    {
      accessorKey: "network",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Network" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 130,
    },
    {
      accessorKey: "angle",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Angle" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 130,
    },
    {
      accessorKey: "date",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Date" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 130,
    },
    {
      accessorKey: "duration",
      enableColumnFilter: true,
      filterFn: multiSelectFilter,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Duration (min)" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      size: 90,
    },
    {
      accessorKey: "initialInterviewCost",
      id: "initialInterviewCost",
      size: 120,
      header: ({ column, table }) => (
        <ColumnHeader column={column} table={table} title="Interview Cost" />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      enableHiding: true,
      meta: { isHiddenByDefault: true },
    },
    {
      accessorKey: "questionnaireCost",
      id: "questionnaireCost",
      size: 120,
      header: ({ column, table }) => (
        <ColumnHeader
          column={column}
          table={table}
          title="Questionnaire Cost"
        />
      ),
      cell: EditableCellRenderer,
      enableResizing: true,
      enableHiding: true,
      meta: { isHiddenByDefault: true },
    },
    {
      id: "totalCost",
      accessorFn: (row) => calculateTotalCost(row),
      size: 120,
      header: ({ column }) => (
        <SimpleColumnHeader column={column} title="Total Cost" />
      ),
      cell: ({ row }) => {
        const interview = row.original;
        const totalCost = calculateTotalCost(interview);
        return (
          <div className="min-h-[36px] flex items-center justify-end">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {formatCurrency(totalCost)}
            </span>
          </div>
        );
      },
      enableResizing: true,
      enableSorting: true,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
  ].map((col) => ({
    ...col,
    enableSorting: true,
  }));

/**
 * Adds global styles needed for table resizing
 */
export function addGlobalStyles() {
  // Add a style element to the document head if it doesn't exist already
  if (!document.getElementById("table-resize-styles")) {
    const style = document.createElement("style");
    style.id = "table-resize-styles";
    style.innerHTML = `
      .resizer {
        position: absolute;
        right: 0;
        top: 0;
        height: 100%;
        width: 5px;
        background-color: #e5e7eb;
        opacity: 0;
        cursor: col-resize;
        user-select: none;
        touch-action: none;
        z-index: 1;
      }
      
      .resizer:hover, 
      .resizer:active {
        opacity: 1 !important;
        background-color: #5CB85C !important;
      }

      th:hover .resizer {
        opacity: 0.5;
      }

      .table-fixed {
        table-layout: fixed;
        width: 100%;
      }
      
      .table-fixed th, 
      .table-fixed td {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `;
    document.head.appendChild(style);
  }
}
